<!--
This page should display the following:
- Contact Person
- Intro text
- Topic, dropdown contains:
    - Sales inquiry
    - Quality
    - Appllication
    - Simulation / Modeling
    - Other
- Message field
- Upload attachment
- Submit button
-->

<template>
    <div class="registration-wrapper">
        <top-bar active-item="support"/>
        <div class="row row--flex row__main-container">
            <div class="col-md-6 col-spacing-75 main-container__form-container">
                <div class="parametric-intro">
                    <h3 class="page-title">My Support</h3>
                </div>

                <contact-card v-if="!isAverageUser && contact" class="form-container__contact-card"
                              :contact="contact"></contact-card>


                <alert v-if="success" message="Success: Your ticket has been submitted successfully!" color="green"></alert>
                <alert v-if="submitError" message="Failure: Something went wrong while submitting the ticket. Please try again later." color="red"></alert>

                <p>
                    No matter where you are located, our sales representatives are never far away.
                    They will always be attentive to your feedback and your requests in order to provide you with a responsive service and an engaging experience with Ampleon.
                </p>

                <form @submit.prevent="handleSubmit">

                    <div class="row form-row">
                        <div class="form-group col-md-12 from-topic-group">
                            <select id="country"
                                    v-model="request.topic"
                                    class="form-control"
                                    :class="{ 'is-invalid': formErrors && $v.request.topic.$error }">
                                <option value="" disabled hidden>Chose a topic.*</option>
                                <option v-for="topic in topics" :value="topic.code">
                                    {{topic.text}}
                                </option>
                            </select>
                            <p v-if="formErrors && !$v.request.topic.required" class="invalid-feedback">Please select a
                                topic.</p>
                        </div>
                    </div>


                    <div class="row form-row">
                        <div class="form-group col-md-12">
                            <textarea v-model="request.message"
                                      id="description"
                                      placeholder="Describe your problem.*"
                                      class="form-control"
                                      :class="{ 'is-invalid': formErrors && $v.request.message.$error }">
                            </textarea>
                            <div v-if="formErrors && $v.request.message.$error" class="invalid-feedback">
                                <p>Please describe your problem.</p>
                            </div>
                        </div>
                    </div>


                    <div class="row form-row">
                        <div class="form-group col-md-12">
                            <file-upload ref="fileUpload" maxAttachmentCount="5" v-on:change="getFiles"/>
                        </div>
                    </div>

                    <div class="row form-row">
                        <div class="form-group col-md-12">
                            <button type="submit"
                                    :disabled="isPending"
                                    class="btn btn-primary register-button"
                            >Submit
                                <i v-show="isPending" class='fa fa-circle-o-notch fa-spin'></i>
                            </button>
                        </div>
                    </div>
                </form>
            </div>
            <div class="col-md-6 image-section">
                <div class="overlay">
                    <div class="svg-wrapper">
                        <svg version="1.1" id="Layer_1" xmlns="http://www.w3.org/2000/svg"
                             xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px"
                             viewBox="0 0 302 172" style="enable-background:new 0 0 302 172;" xml:space="preserve">
                            <path fill="#ffffff" class="st0" d="M151,0.9c21.6,0,31.3,11,34.8,38.1L198,139.2c1,8.1,1.9,9.3,5.8,9.3c3.3,0,4.4-1.2,5.4-9.3l12.2-100.1
                                c3.3-27.2,13.7-38.1,34.8-38.1c23,0,33.4,11,35.4,38.1l9.1,120c0.8,9.5-1.7,12-11.2,12h-3.9c-9.5,0-12-2.3-12.6-12l-8.3-119.8
                                c-0.8-11.2-3.1-14.1-9.1-14.1c-5.2,0-7.9,2.9-9.1,14.1l-14.1,104.3c-2.9,20.5-9.1,27.6-28.8,27.6c-17.4,0-25.9-6.8-28.8-27.6
                                L160.5,39.3c-1.2-11.2-3.9-14.1-9.3-14.1h-0.4c-5.4,0-8.1,2.9-9.3,14.1l-14.3,104.3c-2.9,20.7-11.4,27.6-28.8,27.6
                                c-19.7,0-25.9-7-28.8-27.6L55.5,39.3c-1.2-11.2-3.9-14.1-9.1-14.1c-6,0-8.3,2.9-9.1,14.1l-8.3,119.8c-0.8,9.5-3.1,12-12.6,12h-3.9
                                c-9.5,0-12-2.3-11.2-12l9.1-120C12.3,11.9,22.7,0.9,45.7,0.9c20.9,0,31.3,11,34.8,38.1l12.2,100.1c1,8.1,2.1,9.3,5.4,9.3
                                c3.9,0,4.8-1.2,5.8-9.3l12.2-100.1C119.7,11.7,129.4,0.9,151,0.9z"/>
                        </svg>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import {required} from "vuelidate/lib/validators";
    import ContactCard from "../components/ContactCard";
    import TopBar from "../components/TopBar";
    import FileUpload from "../components/FileUpload";
    import {
        FETCH_MY_SUPPORT_CONTACT,
        FETCH_MY_SUPPORT_TOPICS,
        SEND_MY_SUPPORT_TICKET
    } from "../store/actions.type";
    import {mapGetters} from "vuex";
    import Alert from "../components/Alert";
    const requestInitialState =  Object.freeze({
        topic: '',
        message: '',
        files: []
    });
    export default {
        name: 'Support',
        components: {
            'contact-card': ContactCard,
            'top-bar': TopBar,
            'file-upload': FileUpload,
            'alert': Alert
        },
        data() {
            return {
                request: {
                    topic: requestInitialState.topic,
                    message: requestInitialState.message,
                    files: requestInitialState.files
                },
                formErrors: false,
                isPending: false,
                success: false,
                submitError: false
            }
        },
        computed: {
            ...mapGetters("mySupport", ["contact", "topics"]),
            ...mapGetters(["isAuthenticated", "isAverageUser"]),
        },
        watch: {},
        validations: {
            request: {
                topic: {required},
                message: {required}
            }
        },
        mounted() {
            if (this.isAuthenticated && !this.isAverageUser) {
                this.requestContactPerson();
            }
            this.requestTopics().then(() => {
                if(this.$route.query.topic) {
                    this.request.topic = this.$route.query.topic;
                }
            });
        },
        methods: {
            async handleSubmit(e) {
                // stop here if form is invalid
                this.submitError = false;
                this.success = false;
                this.$v.$touch();
                if (this.$v.$invalid) {
                    this.formErrors = true;
                } else {
                    this.isPending = true;
                    await this.dispatch(SEND_MY_SUPPORT_TICKET, this.request)
                        .then(() => {
                            this.success = true;
                            this.resetRequest();
                        })
                        .catch((e) => {
                            console.error(e);
                            this.submitError = true
                        });
                    this.isPending = false;
                }
            },
            resetRequest() {
                this.request.message = requestInitialState.message;
                this.request.topic = requestInitialState.topic;
                this.request.files = requestInitialState.files;
                this.$refs.fileUpload.removeAllAttachments();
                console.log(this.request, requestInitialState)
            },
            getFiles(files) {
                this.request.files = files;
            },
            async requestContactPerson() {
                await this.dispatch(FETCH_MY_SUPPORT_CONTACT)
            },
            async requestTopics() {
                await this.dispatch(FETCH_MY_SUPPORT_TOPICS)
            },
            dispatch(mutation, payload) {
                return this.$store.dispatch('mySupport/' + mutation, payload);
            }
        }
    }
</script>

<style scoped lang="scss">
    .sidebar, .banner {
        display: none;
    }

    .content {
        left: 0;
        width: 100% !important;
    }

    h3.page-title {
        color: #000 !important;
        font-size: 24px;
        line-height: 29px;
    }

    .row__main-container {
        min-height: 100vh;

        @media (max-width: 991px) {
            flex-direction: column-reverse;
            min-height: auto;
        }

        .parametric-intro {
            margin: 0;
        }

        .page-title {

            @media (max-width: 767px) {
                padding: 15px 0 !important;
            }
        }

        .image-section {

            @media (max-width: 991px) {
                height: 270px;
            }

            @media (max-width: 767px) {
                height: 402px;
            }
        }
    }

    .row--flex {
        display: flex;
        flex-wrap: wrap;
        margin: 0;
    }

    .image-section {
        display: flex;
        flex-direction: column;
        font-family: "Open Sans", sans-serif;
        position: relative;
        height: auto;
        background-image: url("../assets/images/register-img.jpg");
        background-position: center;
        -webkit-background-size: cover;
        background-size: cover;
        background-repeat: no-repeat;
    }

    .overlay {
        opacity: 0.8;
        color: #fff;
        background: #003B71;
        width: 100%;
        height: 100%;
        z-index: 1;
        position: absolute;
        left: 0;
        top: 0;
    }

    .svg-wrapper {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 300px;
        height: 170px;

        path {
            fill: #ffffff;
        }
    }

    .col-spacing-75 {
        padding: 0 75px;

        @media (max-width: 991px) {
            padding: 0 25px;
        }

        @media (max-width: 767px) {
            padding: 0 15px;
        }
    }

    .form-check-label {
        margin: 0 0 0 20px;
        font-size: 12px;
        font-weight: 300;
        line-height: 13px;
        transform: translateY(-2px);
    }

    .form-textarea {
        width: 100%;
    }

    .invalid-feedback {
        color: red;
        margin-left: 12px;
        margin-bottom: 0;
    }

    .parametric-intro {
        margin: 0;
        padding-top: 43px !important;
    }

    .form-control {
        padding: 5px 18px;
        border-color: #ddd;
        box-shadow: none;
        font-size: 14px;
        line-height: 20px;

        &::-webkit-input-placeholder { /* Edge */
            color: #000;
            font-size: 14px;
            font-weight: lighter;
        }

        &:-ms-input-placeholder { /* Internet Explorer 10-11 */
            color: #000;
            font-size: 14px;
            font-weight: lighter;
        }

        &::placeholder {
            color: #000;
            font-size: 14px;
            font-weight: lighter;
        }
    }

    .form-group {
        margin-bottom: 22px;
    }

    .main-container__form-container {
        display: flex;
        flex-direction: column;
    }

    .form-container__contact-card {
        margin-top: 0;
        align-self: baseline;
    }

    .main-container__success-container {
        justify-content: center;
        text-align: center;
        align-items: center;
        padding-top: 2rem;
    }
</style>
